import { createContext } from 'react';

interface Establishment {
    id: string;
    name: string;
    description?: string;
    image: string;
    gallery?: string[];
    video?: string;
    address: string;
    phone?: string;
    openHours?: string;
    category?: { label: string };
    latitude?: number;
    longitude?: number;
}

export interface Merchant {
    id: string;
    email: string;
    establishmentId: string;
    establishment: Establishment;
    /** True quando ainda deve trocar a senha provisoria. */
    mustChangePassword?: boolean;
}

export interface MerchantContextType {
    merchant: Merchant | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (merchant: Merchant) => void;
    logout: () => Promise<void>;
    refreshMerchant: () => Promise<Merchant | null>;
}

export const MerchantContext = createContext<MerchantContextType | undefined>(undefined);

/** Interpreta flags vindas do PostgREST/JSON (boolean, string "true", numero 1, etc.). */
function coercePasswordChangeComplete(value: unknown): boolean {
    if (value === true || value === 1) return true;
    if (typeof value === 'string') {
        const s = value.trim().toLowerCase();
        return s === 'true' || s === 't' || s === '1' || s === 'yes';
    }
    return false;
}

/** Valor cru no banco: true = senha definitiva ja concluida; false = ainda pendente. */
function pickDbPasswordChangeComplete(m: Record<string, unknown>): boolean {
    return (
        coercePasswordChangeComplete(m.mustChangePassword) ||
        coercePasswordChangeComplete(m.must_change_password) ||
        coercePasswordChangeComplete(m.mustchangepassword)
    );
}

/** RPC/login as vezes devolve `establishment_id` em vez de `establishmentId`. */
export function normalizeMerchantFromRpc(row: unknown): Merchant {
    const m = row as Merchant & { establishment_id?: string; must_change_password?: boolean };
    const establishmentId =
        m.establishmentId ||
        m.establishment_id ||
        m.establishment?.id ||
        '';
    const r = m as unknown as Record<string, unknown>;
    const hasPasswordFlagInPayload =
        r.mustChangePassword !== undefined ||
        r.must_change_password !== undefined ||
        r.mustchangepassword !== undefined;
    const dbCompleted = hasPasswordFlagInPayload ? pickDbPasswordChangeComplete(r) : true;
    const mustChangePassword = !dbCompleted;
    return {
        ...m,
        establishmentId,
        establishment: m.establishment ?? ({} as Establishment),
        mustChangePassword,
    };
}
